/**
 * 보고서 작성 방식 (Amendment A30, 2026-10-24 지도감독 피드백 반영)
 *
 * 앱은 "자료"를 모으고, 문서 문장은 외부 AI가 문서 전체를 한 흐름으로 쓴다 (칸마다 따로 쓰지 않음).
 * - 사실 칸(인적사항·회기 표·검사 점수·축어록 본문 등)은 앱이 채우고 잠근다. AI 답에 있어도 반영하지 않는다.
 * - 지도감독 원칙과 섹션별 작성 기준을 매번 함께 보낸다 (원칙은 설정에서 고칠 수 있다).
 * - 꼭 들어가야 할 항목을 정해 보내고, 붙여넣은 답에 빠졌는지 확인한다.
 * - 답을 반영하기 전에 전사 번호(g12 등)를 지우고, 기관·학교·학과·교회 이름을 ○○로 가린다.
 */
import type { DocumentArtifact } from '../schema/v1';
import { BEGIN, END } from './prompts';
import { maskInstitutions } from '../workflow/mask';
import { fieldBody, setFieldBody } from './docDraft';

/** 문서 전체를 한 번에 쓰는 문서 (대화록은 작성표 방식이라 따로) */
export type WholeDocType = Extract<
  DocumentArtifact['documentType'],
  'case_report' | 'termination_report' | 'session_record' | 'verbatim'
>;
export const isWholeDocType = (t: DocumentArtifact['documentType']): t is WholeDocType =>
  t === 'case_report' || t === 'termination_report' || t === 'session_record' || t === 'verbatim';

/** 지도감독 원칙 (기본값). 설정에서 상담자가 고치거나 새 피드백을 덧붙일 수 있다 */
export const DEFAULT_SUPERVISION_GUIDELINES = `[지도감독 원칙 — 모든 보고서]
1. 인적사항은 최소한으로 쓴다. 기관·학교·학과·교회·직장 이름은 ○○로 가린다 (예: ○○대학교 대학원 ○○과 석사과정). 지역, 가족의 이름, 구체적인 직장처럼 내담자를 알아볼 수 있는 세부는 쓰지 않는다.
2. 주호소와 관련된 사건은 나열하지 않는다. 그 사건에서 드러난 심리적 어려움이 어떤 행동패턴(문제행동)으로 나타나는지 연결해서 쓴다.
3. 신앙생활은 "신앙이 흔들린다" 같은 일반적인 말로 쓰지 않는다. 예배·기도·공동체·하나님 이해에서 무엇이 어떻게 달라졌는지 내담자의 실제 표현으로 구체화한다.
4. 문제행동은 추론이 아니라 내담자의 실제 발화를 근거로 쓴다. 근거는 짧게 인용하고 회기를 밝힌다 (예: 3회기 "…"). 근거가 없는 추론은 쓰지 않는다.
5. 상담목표는 목표마다 '어떻게' 도달할지(구체적인 방법, 단계, 도달했는지 확인하는 방법)를 함께 쓴다.
6. 심리검사는 가능한 한 1회기에 실시하고 2회기에 종합적으로 해석한다. 보고서에는 검사 결과를 주호소·행동패턴과 연결해서 쓴다.
7. 이론은 나열하지 않는다. 메인 이론 하나로 끌고 간다: 그 이론으로 이 내담자의 행동패턴을 어떻게 이해하고, 그래서 어떤 방향과 단계로 개입하는지를 설명한다. 다른 이론은 필요할 때만 보조로 쓴다.
8. 상담자 평가에서는 지도감독에서 지적된 점을 정직하게 다룬다: 내담자의 알아차림을 돕는 반영·구체화가 부족했던 지점, 돌보려는 의도가 장황한 설명으로 나타난 지점, 판단을 멈추고 내담자의 말을 따라가며 초점화하지 못한 지점.

[문체]
- 항목을 나열하지 않고 묶어서 문단으로 쓴다. 같은 내용을 여러 칸에 반복하지 않는다.
- 회기 번호와 축어록 번호(상N/내N)만 인용한다. 전사 번호(g12, i45 같은 것)는 쓰지 않는다.
- "추정", "(AI 초안)", "확인 필요" 같은 표시를 남기지 않는다. 해석은 "~로 보인다" 정도로 자연스럽게 쓴다.
- 서술형(~이다, ~하였다)으로 한 문서 안에서 통일한다.`;

/** 섹션별 작성 기준 (칸 제목의 앞부분 → 기준) */
const CASE_REPORT_CRITERIA: [string, string][] = [
  [
    '내담자가 직접 표현한 호소',
    '내담자의 실제 표현 2~4개를 회기와 함께 짧게 인용하고, 그 표현들이 공통으로 가리키는 어려움을 한 문장으로 잇는다.',
  ],
  ['주호소 정리', '주호소와 그 배경의 핵심을 2~3문장으로. 사건 나열 금지.'],
  ['2. 내담 경위', '내담 경위와 이전 상담 경험을 2~3문장으로 (기관 이름은 ○○).'],
  ['3. 인상 및 행동 관찰', '관찰 가능한 말투·정서·태도와 회기 중 변화를 3~5문장으로.'],
  [
    '2) 상담자의 해석',
    '검사 결과를 주호소·행동패턴과 연결해 해석한다 (척도 이름과 수준을 밝히고, 그것이 실제 회기에서 어떻게 드러났는지). 검사가 없으면 "실시한 검사 없음".',
  ],
  ['1) 가족구성원', '이름 대신 관계로, 내담자와의 관계 양상 중심으로 문단.'],
  ['6. 사회적 관계', '가족 밖 관계·학교/직장 생활에서 반복되는 관계 방식을 문단으로 (기관 이름은 ○○).'],
  ['1) 초기의 애착 대상과의 관계', '어린 시절 양육자와의 관계를 내담자가 말한 범위에서. 자료가 적으면 짧게.'],
  [
    '2) 청소년 및 성인기',
    '사건을 나열하지 않고, 사건에서 드러난 심리적 어려움과 그것이 어떤 행동패턴으로 이어졌는지 연결 (원칙 2).',
  ],
  ['3) 최근 문제의 발생 및 유지', '최근 문제가 무엇을 계기로 시작되고 무엇 때문에 유지되는지.'],
  ['8. 내담자의 강점과 약점', '강점과 약점을 각각 문단으로, 상담에 어떻게 작용하는지까지.'],
  ['1) 내담자의 하나님 이해', '내담자의 실제 표현으로 구체화 (원칙 3).'],
  ['2) 내담자의 신앙생활에 대한 이해', '예배·기도·공동체 등에서 무엇이 어떻게 달라졌는지 구체적으로 (원칙 3).'],
  ['3) 내담자의 인간에 대한 이해', '자기와 타인을 어떻게 보는지, 실제 표현 근거.'],
  ['① 주호소문제 및 문제행동', '문제행동을 실제 발화를 근거로 정리 (원칙 4).'],
  ['② 문제행동 이면의 유발요인', '유발요인(신앙 요인 포함)을 근거와 함께. 가설은 가설로.'],
  ['③ 문제행동을 촉발·유지시키는 과정', '무엇이 촉발하고 무엇이 유지시키는지 과정으로 서술.'],
  [
    '관련 상담 이론에 근거한 통합 가설',
    '메인 이론 관점의 핵심 가설을 먼저 한 문장으로 쓰고("한 가지로 정리하면"), 이를 지지하는 행동패턴 2~3개를 실제 발화로 뒷받침한다. 이어서 촉발 요인 → 유발 요인 → 유지 요인 → 대처방식 → 대인관계 패턴이 어떻게 이어지는지, 이 이론을 택한 이유와 그래서 어떤 목표·전략 방향인지까지 한두 문단으로 (지도감독에서 받은 순서).',
  ],
  ['1) 상담자 자신의 하나님이해', '상담자의 실제 개입·반응에서 드러난 하나님 이해를 자연스러운 문장으로.'],
  ['2) 상담자 자신의 인간이해', '상담자의 인간 이해가 이 사례에서 어떻게 작동했는지.'],
  ['3) 상담자로서 이해하는', '신앙인으로서 상담자의 자세와 삶이 이 사례와 어떻게 만났는지.'],
  ['4) 내담자에 대한 상담자의 신학적 관점', '내담자를 신학적으로 어떻게 이해하는지, 이 사례의 구체적 장면과 연결.'],
  ['1) 상담자로서 택한 상담이론', '메인 이론 하나와 그 이론을 택한 이유를 이 내담자의 행동패턴과 연결해서 (원칙 7).'],
  ['2) 사례(내담자)를 위한 상담방법론', '메인 이론에 따른 진행 방향과 단계를 이 사례에 맞게.'],
  ['3. 상담자의 학문(신학과 상담심리학)간 대화', '신학과 상담심리학이 이 사례에서 어떻게 만나는지 통합 서술.'],
  ['1) 합의한 상담 목표', '내담자의 표현으로 쓴 목표마다 어떻게 도달할지(방법·단계·확인 방법)를 함께 (원칙 5).'],
  ['2) 임상적 상담 목표', '상담자의 임상적 목표마다 어떻게 도달할지를 함께 (원칙 5).'],
  [
    '2. 상담 전략',
    '주요 전략 2~3개로 묶고, 각각 무엇을 하는지(방법), 왜 이 내담자에게 맞는지(근거), 예상되는 어려움과 대처를 문단으로. 기법은 나열하지 않고 그 안에 녹인다: 메인 이론의 흐름 안에서 어떤 기법을 언제 왜 썼고 내담자가 어떻게 반응했는지, 앞으로 어떻게 이어갈지.',
  ],
  [
    '1회기',
    '이 회기의 주제 → 핵심 흐름 → 상담자 개입과 내담자 반응 → 다음 회기로의 연결을 3~4문장으로. 대화 내용을 옮기지 않는다. 첫 줄의 "날짜: …"는 그대로 둔다.',
  ],
  [
    '축어록 (상: 상담자',
    '보고서용으로 정돈한다: 주고받는 대화로 자연스럽게 읽히게, 말한 뜻과 순서는 그대로. 화자가 애매한 줄은 문맥으로 상담자/내담자를 정하고(불명으로 두지 않음), 전사 오류로 보이는 말은 문맥에 맞게 바로잡고, 조각난 말은 한 문장으로 잇고, 의미 없는 추임새·맞장구·말 더듬기는 정리한다. 내용을 요약하거나 새로 지어내지 않는다. 한 줄에 한 발언, "상1: …", "내1: …" 형식으로 화자별 번호를 처음부터 다시 매긴다.',
  ],
  [
    '1) 이 부분을 축어록으로 만든 이유',
    '이 구간에 담긴 핵심 포인트(메인 이론에 따른 개입의 흔적, 내담자의 알아차림·변화)를 한 문단으로.',
  ],
  ['1) 내담자의 평가', '내담자가 변화나 상담에 대해 실제로 말한 것.'],
  ['2) 상담자의 평가', '목표별 진행과 변화, 남은 과제를 근거와 함께 문단으로.'],
  [
    '2. 상담자 자신에 대한 평가',
    '원칙 8에 따라 정직하게. 잘한 점과 다르게 할 수 있었던 점을 구체적인 장면(회기, 축어록 번호)과 함께.',
  ],
  ['수퍼비전 질문', '이 사례에서 실제로 막혔던 지점에 대한 구체적인 질문 3~5개.'],
  ['1. 목회상담으로서 본 상담', '나열 없이 통합 서술. 이 사례의 구체적인 장면과 연결된 목회신학적 평가.'],
  ['2. 본 상담을 목회상담이라고 부를 수 있는', '나열 없이 통합 서술. 이 상담이 목회상담인 이유를 구체적 장면과 함께.'],
];

const TERMINATION_CRITERIA: [string, string][] = [
  ['주 호소 문제', '주호소를 2~3문장으로.'],
  ['1) 합의한 상담 목표', '목표마다 어떻게 접근했는지 함께.'],
  ['2) 임상적 상담 목표', '목표마다 어떻게 접근했는지 함께.'],
  ['IV. 상담 전략', '메인 이론에 따른 전략을 2~3개로 묶어 문단으로.'],
  ['1. 상담 목표 달성 정도', '목표별 달성 정도를 실제 변화(내담자의 표현)를 근거로.'],
  ['2. 상담 경과', '초기·중기·후기 흐름을 메인 이론의 단계와 연결해 문단으로. 회기 내용 나열 금지.'],
  ['1) 변화된 점', '변화를 내담자의 실제 표현과 행동 변화로.'],
  ['2) 남아 있는 어려움', '남은 어려움과 그 이유.'],
  ['3) 보호요인 및 종결 후 제언', '보호요인과 구체적인 제언.'],
  ['상담자 소감 및 배운 점', '정직한 성찰을 문단으로 (원칙 8).'],
];

/** 상담기록지 (한 회기): 기록지 양식 그대로, 회기 흐름이 이어지게 */
const SESSION_RECORD_CRITERIA: [string, string][] = [
  ['상담 계기/목표', '상담 신청 계기와 합의 목표를 2~3문장으로. 목표는 내담자의 표현을 살린다.'],
  ['과거 상담경험', '이전 상담 경험과 그때 도움이 된 점·어려웠던 점을 1~2문장으로. 없으면 "없음".'],
  [
    'Important issue',
    '이번 회기에 가장 중요하게 다룬 문제를 주 이론의 관점에서 2~3문장으로. 내담자의 실제 표현 한두 개를 짧게 인용한다.',
  ],
  ['전 회기 issue/과제', '지난 회기에서 이어진 주제와 과제를 1~2문장으로. 첫 회기면 "첫 회기".'],
  ['과제 수행 여부', '지난 회기 과제를 했는지와 내담자의 반응을 1문장으로. 과제가 없었으면 "해당 없음".'],
  [
    '상담 내용',
    '도입 → 핵심 주제 → 상담자 개입과 내담자 반응 → 알아차림·변화 → 마무리의 흐름을 한 문단(4~8문장)으로. 대화를 옮기지 않는다. 위험 관련 내용이 있었으면 반드시 넣는다.',
  ],
  ['다음 회기 계획', '다음 회기에 할 것(목표·확인할 것·과제)을 주 이론의 지금 단계와 연결해 2~4문장으로.'],
  ['비고', '위험·의뢰·행정 사항만. 없으면 비운다.'],
];

/** 축어록: 본문은 앱이 채우고, 이유·질문·과정 분석을 쓴다 */
const VERBATIM_CRITERIA: [string, string][] = [
  [
    '이 구간을 선택한 이유',
    '이 구간에 담긴 핵심 포인트(주 이론에 따른 개입의 흔적, 내담자의 알아차림·변화)를 한 문단으로.',
  ],
  [
    '수퍼비전 질문',
    '이 구간에서 실제로 막혔거나 다르게 할 수 있었던 지점에 대한 구체적인 질문 3~5개. 상N/내N 번호를 함께.',
  ],
  [
    '상담자 소감 · 과정 분석',
    '구간의 흐름을 상N/내N 번호로 짚으며 상담자 개입의 의도와 효과, 내담자의 반응, 잘한 점과 다르게 할 수 있었던 점을 정직하게 (원칙 8). 문단으로.',
  ],
];

export const SECTION_CRITERIA: Record<WholeDocType, [string, string][]> = {
  case_report: CASE_REPORT_CRITERIA,
  termination_report: TERMINATION_CRITERIA,
  session_record: SESSION_RECORD_CRITERIA,
  verbatim: VERBATIM_CRITERIA,
};

/** 앱이 채우고 잠그는 사실 칸 (AI 답에 있어도 반영하지 않음) */
export const LOCKED_HEADINGS: Record<WholeDocType, string[]> = {
  case_report: [
    '1. 인적 사항',
    '1) 실시한 검사',
    '2) 가계도',
    '3. 상담진행 과정 및 회기 주제',
    '2) 시작과 끝나는 시간',
    // 검사 문항별 응답(내담자가 쓴 그대로)은 다듬지 않는다
    '별지 ',
  ],
  termination_report: ['1. 상담 진행 기간', '2. 총 상담 횟수', '3. 상담 종결 사유', '내담자의 표현'],
  session_record: ['검사'],
  verbatim: ['축어록 (상: 상담자'],
};

export const isLockedHeading = (type: WholeDocType, heading: string) =>
  LOCKED_HEADINGS[type].some((h) => heading.trim().startsWith(h));

/** 이 칸의 작성 기준 (회기 요약은 'N회기' 모두 같은 기준) */
export function criteriaFor(type: WholeDocType, heading: string): string | undefined {
  const h = heading.trim();
  if (type === 'case_report' && /^\d+회기$/.test(h)) return CASE_REPORT_CRITERIA.find(([k]) => k === '1회기')![1];
  return SECTION_CRITERIA[type].find(([k]) => h.startsWith(k) || h.includes(k))?.[1];
}

/** 꼭 들어가야 할 항목: 라벨 + 문서에서 찾을 말(하나라도 있으면 들어간 것으로 봄) */
export type MustInclude = { label: string; terms: string[]; critical?: boolean };

const squash = (s: string) => s.replace(/\s+/g, '').toLowerCase();

/** 답(문서 전체)에 꼭 넣을 항목이 들어갔는지: 빠진 항목만 돌려준다 */
export function missingItems(content: string, items: MustInclude[]): MustInclude[] {
  const hay = squash(content);
  return items.filter((it) => !it.terms.some((t) => t.trim() && hay.includes(squash(t))));
}

/** 문장에서 핵심어를 뽑는다 (꼭 넣을 항목 확인용): 2글자 이상 명사 비슷한 덩어리 중 긴 것 몇 개 */
export function keyTerms(text: string, n = 3): string[] {
  const words = text
    .replace(/["'“”‘’()[\]{}.,!?·…:;~-]/g, ' ')
    .split(/\s+/)
    .map((w) => w.replace(/(을|를|이|가|은|는|에|에서|으로|로|과|와|의|도|만|하다|한다|했다|하는|하고)$/u, ''))
    .filter((w) => w.length >= 2);
  return [...new Set(words)].sort((a, b) => b.length - a.length).slice(0, n);
}

/** 전사 번호 지우기: (S2 g248-1), [g12 05:23 내?], g12-0, i45 같은 표시 */
export function stripTranscriptRefs(text: string): string {
  return text
    .replace(/\s*\[[gi]\d+(?:-\d+)?[^\]]*\]/g, '')
    .replace(/\s*\((?:S\d+\s*)?(?:[gi]\d+(?:-\d+)?(?:\s*[,·~–-]\s*)?)+\)/g, '')
    .replace(/(S\d+\s+)?\b[gi]\d+(?:-\d+)?\b(\s*[,·~–-]\s*[gi]\d+(?:-\d+)?)*/g, (_m, s?: string) => (s ? s.trim() : ''))
    .replace(/\(\s*\)/g, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/ +([,.)])/g, '$1');
}

export { maskInstitutions };

/** 붙여넣은 문서 문장을 다듬는다 (전사 번호 지우기 + 이름 가리기) */
export function cleanReportText(text: string): string {
  return maskInstitutions(stripTranscriptRefs(text));
}

/** 문서 전체를 한 번에 쓰는 요청 */
export function buildWholeDocPacket(i: {
  type: WholeDocType;
  title: string;
  /** 지금 문서 (칸 제목과 앱이 모은 자료, 식별정보 없음) */
  content: string;
  /** 추가 자료 (회기별 요약·분석·근거·목표 등) */
  extras: string;
  guidelines: string;
  mustInclude: MustInclude[];
  request?: string;
  /** 분량 목표 (예: "8~10쪽"). 서술 칸 글자 수 예산을 함께 알려 준다 */
  length?: { pages: string; proseChars?: number; proseFields?: number };
}): string {
  const fieldHeadings = i.content
    .replace(/\r/g, '')
    .split('\n')
    .filter((l) => /^#{2,4}\s/.test(l));
  const plan = fieldHeadings.map((l) => {
    const h = l.replace(/^#+\s*/, '').trim();
    if (/^##\s/.test(l)) return l;
    if (isLockedHeading(i.type, h)) return `${l}   ← 앱이 채움 (쓰지 말 것)`;
    const c = criteriaFor(i.type, h);
    return c ? `${l}\n   기준: ${c}` : l;
  });
  return [
    `=== WHOLE_DOC_PACKET_V1 ===`,
    `[문서] ${i.title}`,
    '',
    i.guidelines,
    '',
    '[양식과 칸별 작성 기준] (제목을 그대로, 이 순서대로)',
    ...plan,
    '',
    i.mustInclude.length ? '[꼭 들어가야 할 항목] (빠지면 안 됨)' : '',
    ...i.mustInclude.map((m) => `- ${m.label}`),
    '',
    '[지금 문서 — 앱이 모은 자료] (그대로 옮기지 말고 자료로만 쓴다)',
    i.content,
    '',
    '[추가 자료]',
    i.extras,
    '',
    '[상담자 요청]',
    i.request?.trim() || '없음',
    `=== END WHOLE_DOC_PACKET_V1 ===`,
    '',
    '위 자료로 문서 전체를 처음부터 끝까지 하나의 흐름으로 다시 써라. 칸마다 따로 쓰지 말고, 앞에서 쓴 내용과 이어지게 쓴다.',
    '- 지도감독 원칙과 칸별 기준을 지킨다. 자료에 없는 사실은 만들지 않는다.',
    ...WRITING_RULES,
    ...(i.length
      ? [
          `- 분량: 별지를 뺀 본문이 A4 ${i.length.pages} (함초롬돋움 10pt, 표·축어록 포함). 이 분량은 모든 칸을 다 채운 상태의 분량이다 — 빈 칸을 남겨서 맞추지 않는다.${i.length.proseFields && i.length.proseChars ? ` 서술 칸 ${i.length.proseFields}개를 모두 채운 분량이 표·축어록을 뺀 약 ${i.length.proseChars.toLocaleString('ko-KR')}자 (칸당 평균 약 ${Math.round(i.length.proseChars / i.length.proseFields / 10) * 10}자).` : i.length.proseChars ? ` 표·축어록을 뺀 서술 칸 전체가 약 ${i.length.proseChars.toLocaleString('ko-KR')}자.` : ''} 중요한 칸(사례개념화·상담 전략·평가)은 길게, 덜 중요한 칸은 짧게 나눠 이 분량에 맞춘다.`,
        ]
      : []),
    '- "앱이 채움" 칸은 쓰지 않는다 (제목만 두거나 생략).',
    '- 출력은 마크다운 문서 하나: "# 제목", "## 큰 항목", "### / #### 칸 제목"을 양식과 똑같이 쓰고 그 아래 본문을 쓴다. 앞뒤 설명은 쓰지 않는다.',
    `- 출력 맨 앞에 ${BEGIN}, 맨 끝에 ${END} 를 한 줄씩 쓴다.`,
  ]
    .filter((l, k, a) => !(l === '' && a[k - 1] === ''))
    .join('\n');
}

/** 상담자를 가리키는 말: 기술하는 부분은 3인칭 "상담자", 개인적 성찰 부분은 1인칭 */
export const SELF_REFERENCE_RULES = [
  '- 상담자를 가리킬 때: 내담자 이해·상담 목표와 전략·회기 요약·축어록 설명·상담 평가처럼 사례를 기술하는 부분은 "상담자"(3인칭)로 쓴다. IV 상담자 자신에 대한 이해, VI의 상담자 자신에 대한 평가, VII 수퍼비전 질문, VIII 신학적 성찰처럼 개인적 성찰을 쓰는 부분은 1인칭("나는", "내가", "나의")으로 쓰고 "상담자"라고 부르지 않는다. "상담사"라는 말은 어디에도 쓰지 않는다. 내담자는 늘 "내담자"로 (그/그녀·이름으로 부르지 않음).',
];

/** 사람이 직접 쓴 보고서처럼: AI·앱 흔적을 남기지 않는다 */
export const NO_TRACE_RULES = [
  '- AI나 앱이 쓴 흔적을 남기지 않는다: 대괄호 꼬리표([…]), "(추정)", "(확인 필요)", 전사 번호, 마크다운 강조(**), 줄표(—)로 이어 붙인 문장, 괄호 속 영어 병기 남발, 안내문 말투("~할 수 있습니다", "~입니다"), 정리용 머리말("정리하면", "결론적으로", "요약하면"), 같은 말 되풀이. 한 사람이 직접 쓴 보고서처럼 서술형(~이다, ~하였다)으로 통일한다.',
];

/** 문서를 쓸 때 늘 지키는 것 (설정의 지도감독 원칙과 별도로 항상 보낸다) */
export const WRITING_RULES = [
  '- 양식(제목·부제·항목 이름과 순서)은 한 글자도 바꾸지 않는다. 새 항목을 만들거나 항목을 빼지 않는다.',
  '- 서술 칸은 목록·글머리표(•, -, 1.)로 나열하지 않고, 종합해서 이어지는 문단으로 쓴다. 앱이 모은 목록(예: "취약요인: …", "신념: …", "유지: …")은 재료일 뿐 그대로 옮기지 않는다. 표·축어록·회기 표는 예외.',
  '- 상담 전략은 줄줄이 나열하지 않는다. 주요 전략 2~3개만 골라, 각각 무엇을 어떻게 하는지(방법), 왜 이 내담자에게 맞는지(근거), 예상되는 어려움과 대처까지 구체적으로 설명한다.',
  '- [상담자 메모]와 앱 자료에 따로 적힌 성찰·메모는 따로 두지 않고, 알맞은 항목(IV 상담자 자신에 대한 이해, VI 상담자 자신에 대한 평가 등) 안에 녹여 해석해서 쓴다.',
  '- 모든 칸을 빠짐없이 쓴다. "[상담자 직접 작성 필요]", "[자료 부족 …]"으로 남기지 않는다. 개인적 성찰 칸(IV 상담자 자신에 대한 이해, VI 상담자 자신에 대한 평가, VII 수퍼비전, VIII 신학적 성찰)도 회기 기록·축어록에서 드러난 상담자의 개입과 반응, [상담자 메모]를 근거로 1인칭 초안을 쓴다 (상담자가 읽고 고친다). 자료에 붙은 버전·출처 표시(예: "(사례개념화 v1 기준)")는 본문에 남기지 않는다.',
  '- 내담자 이름은 {{이름}} 그대로 둔다 (앱이 성○○으로 바꾼다). 학교·기관·교회·직장 이름은 ○○로 가린다 (예: ○○대 ○○과, ○○교회).',
  ...SELF_REFERENCE_RULES,
  ...NO_TRACE_RULES,
];

/** 다듬기 요청 (같은 대화에서): 방금 쓴 문서를 다시 읽고 전체를 다시 출력 */
export const POLISH_PROMPT = `방금 쓴 문서 전체를 다시 읽고 다듬어서, 같은 양식으로 전체를 다시 출력해 주세요.
- 나열된 부분은 묶어서 문단으로, 칸끼리 반복되는 내용은 한 곳에만.
- 근거 없는 추론은 지우고, 문제행동은 실제 발화(회기 표시)로 뒷받침.
- 이론 이름을 늘어놓은 곳은 메인 이론 하나로 정리.
- 전사 번호, "추정", "(AI 초안)" 같은 표시는 지우기. 기관·학교·교회 이름은 ○○.
- AI·앱 흔적(대괄호 꼬리표, 마크다운 강조, 줄표로 이은 문장, 안내문 말투, "정리하면" 같은 머리말)은 지우고, 한 사람이 쓴 보고서처럼 서술형(~이다, ~하였다)으로.
- 사례를 기술하는 부분은 "상담자", 개인적 성찰(IV 상담자 자신에 대한 이해, VI 상담자 자신에 대한 평가, VII 수퍼비전, VIII 신학적 성찰)은 1인칭("나는")으로. "상담사"라는 말은 쓰지 않기.
- 꼭 들어가야 할 항목이 빠지지 않았는지 확인.
- 출력 맨 앞과 끝의 표식(${BEGIN} / ${END})도 그대로.`;

/** 빠진 항목만 보충 요청 */
export function supplementPrompt(missing: MustInclude[]): string {
  return [
    '방금 쓴 문서에 아래 항목이 빠졌습니다. 이 항목이 들어가야 할 칸만 고쳐서, 고친 칸만 같은 양식(칸 제목 + 본문)으로 출력해 주세요.',
    ...missing.map((m) => `- ${m.label}`),
    `출력 맨 앞과 끝에 ${BEGIN} / ${END} 를 한 줄씩.`,
  ].join('\n');
}

// ---------- 붙여넣은 답을 문서에 맞추기 ----------

/** 예전 버전이 만들던 양식 밖 칸 → 그 내용이 들어갈 양식의 칸 */
const OUT_OF_FORMAT: { match: RegExp; target: string }[] = [
  { match: /^주요 호소 문제 서술/, target: '주호소 정리' },
  { match: /^사례개념화 서술/, target: '관련 상담 이론에 근거한 통합 가설' },
  { match: /^상담기법 사용과 계획/, target: '2. 상담 전략' },
  { match: /^상담 평가 서술/, target: '2) 상담자의 평가' },
  { match: /^기록해 둔 상담자 성찰/, target: '2. 상담자 자신에 대한 평가' },
  { match: /^1\. 상담 목표 달성 정도 — 서술/, target: '1. 상담 목표 달성 정도' },
  { match: /^2\. 상담 경과 — 서술/, target: '2. 상담 경과' },
  { match: /^상담기법 사용 정리/, target: '상담 전략 및 주요 개입' },
];

/**
 * 양식은 바꾸지 않는다: 예전 버전이 넣던 양식 밖 칸("… (AI 초안)", "기록해 둔 상담자 성찰")과 "(사례개념화 vN 기준)" 줄을 없앤다.
 * 그 칸에 쓴 내용이 있으면 알맞은 양식 칸으로 옮긴다 (비어 있으면 넣고, 내용이 있으면 뒤에 붙인다).
 */
export function normalizeReportFormat(content: string, type: WholeDocType): string {
  if (type !== 'case_report' && type !== 'termination_report') return content;
  let out = content
    .replace(/\r/g, '')
    .replace(/^\(사례개념화 v\d+ 기준\)\s*\n/m, '')
    .replace(/^(BEGIN|END)_COUNSEL_AI_JSON[ \t]*\n?/gm, '');
  for (const rule of OUT_OF_FORMAT) {
    const lines = out.split('\n');
    const at = lines.findIndex((l) => /^#{3,4}\s/.test(l) && rule.match.test(l.replace(/^#+\s*/, '').trim()));
    if (at < 0) continue;
    let end = at + 1;
    while (end < lines.length && !/^#{1,4}\s/.test(lines[end]!)) end++;
    const moved = lines
      .slice(at + 1, end)
      .filter((l) => !/^\[(AI 초안|상담자 직접 작성 필요|자료 부족)[^\]]*\]$/.test(l.trim()))
      .join('\n')
      .trim();
    lines.splice(at, end - at);
    out = lines.join('\n');
    if (!moved) continue;
    const cur = fieldBody(out, rule.target);
    if (cur === null) continue;
    const empty = !cur.trim() || /^\[(AI 초안|상담자 직접 작성 필요|자료 부족)[^\]]*\]$/.test(cur.trim());
    out = setFieldBody(out, rule.target, empty ? moved : `${cur.trim()}\n\n${moved}`);
  }
  return out;
}

/**
 * 붙여넣은 답(칸 제목 → 본문)을 문서에 넣을 수 있게 정리한다:
 * 잠긴 사실 칸은 빼고, 전사 번호를 지우고 이름을 가리며, 회기 요약 첫 줄의 날짜는 지킨다.
 */
export function prepareWholeDocRevise(
  type: WholeDocType,
  currentContent: string,
  revise: Record<string, string>,
): { revise: Record<string, string>; locked: string[] } {
  const out: Record<string, string> = {};
  const locked: string[] = [];
  for (const [h, text] of Object.entries(revise)) {
    if (isLockedHeading(type, h)) {
      locked.push(h);
      continue;
    }
    let t = cleanReportText(text).trim();
    if (/^\d+회기$/.test(h.trim())) {
      const dateLine = (fieldBody(currentContent, h) ?? '').split('\n').find((l) => l.startsWith('날짜:'));
      if (dateLine && !t.includes('날짜:')) t = `${dateLine}\n${t}`;
    }
    if (t) out[h] = t;
  }
  return { revise: out, locked };
}
